import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register } from '../actions/authActions';
import Message from '../components/ui/Message';
import Loader from '../components/ui/Loader';
import VerificationCodeInput from '../components/ui/VerificationCodeInput';

const RegisterPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [message, setMessage] = useState(null);
  const [userExistsError, setUserExistsError] = useState(false);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const auth = useSelector(state => state.auth);
  const { loading, error, isAuthenticated } = auth;
  
  // Get redirect path from URL query params or default to home
  const query = new URLSearchParams(location.search);
  const redirect = query.get('redirect') || '/';
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirect);
    }
  }, [navigate, isAuthenticated, redirect]);

  // Check if error message indicates user already exists
  useEffect(() => {
    if (error && error.includes('已存在')) {
      setUserExistsError(true);
    } else {
      setUserExistsError(false);
    }
  }, [error]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (password !== confirmPassword) {
      setMessage('两次输入的密码不一致');
      return;
    }
    
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage('请输入6位数字验证码');
      return;
    }
    
    if (!password || password.length < 6) {
      setMessage('密码必须至少有6个字符');
      return;
    }
    
    setMessage(null);
    setUserExistsError(false);
    
    const userData = {
      phone_number: phoneNumber,
      code: verificationCode,
      password,
      full_name: fullName,
      email,
      address
    };
    
    dispatch(register(userData));
  };
  
  const handleVerificationChange = (code) => {
    setVerificationCode(code);
  };

  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(e.target.value);
    // Reset error states when phone number changes
    if (userExistsError) {
      setUserExistsError(false);
      dispatch({ type: 'CLEAR_AUTH_ERROR' });
    }
  };
  
  return (
    <div className="flex justify-center">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold mb-6 text-center">创建账号</h1>
        
        {message && <Message onClose={() => setMessage(null)}>{message}</Message>}
        
        {/* Special handling for user exists error */}
        {userExistsError && (
          <Message variant="error" onClose={() => { setUserExistsError(false); dispatch({ type: 'CLEAR_AUTH_ERROR' }); }}>
            <div>
              <p className="mb-2">{error}</p>
              <div className="flex space-x-2">
                <Link 
                  to={redirect ? `/login?redirect=${redirect}` : '/login'}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                  立即登录
                </Link>
                <Link 
                  to="/password-reset"
                  className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                >
                  忘记密码？
                </Link>
              </div>
            </div>
          </Message>
        )}
        
        {/* Regular error message for other errors */}
        {error && !userExistsError && (
          <Message variant="error" onClose={() => dispatch({ type: 'CLEAR_AUTH_ERROR' })}>
            {error}
          </Message>
        )}
        
        <div className="bg-white p-6 rounded shadow">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">手机号码*</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                placeholder="请输入手机号码"
                className="w-full p-2 border rounded"
                required
              />
            </div>
            
            <div className="mb-4">
              <VerificationCodeInput
                phoneNumber={phoneNumber}
                onVerificationChange={handleVerificationChange}
                verificationType="register"
                disabled={userExistsError} // Disable if user exists
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">密码*</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请创建密码"
                className="w-full p-2 border rounded"
                required
                minLength={6}
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">确认密码*</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="请确认密码"
                className="w-full p-2 border rounded"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">姓名</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="请输入姓名"
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">电子邮箱</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入电子邮箱"
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-700 mb-1">地址</label>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="请输入详细地址"
                className="w-full p-2 border rounded"
                rows={3}
              />
            </div>
            
            <button
              type="submit"
              className={`w-full py-2 rounded ${
                userExistsError
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-red-700 text-white hover:bg-red-800'
              }`}
              disabled={loading || userExistsError}
            >
              {loading ? <Loader size="sm" /> : '注册'}
            </button>
          </form>
          
          <div className="mt-4 text-center">
            已有账号？{' '}
            <Link to={redirect ? `/login?redirect=${redirect}` : '/login'} className="text-red-700 hover:underline">
              登录
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;